import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, X } from "lucide-react";

// Hidden on these routes — they already have their own strong CTAs.
const HIDDEN_ON = ["/apply", "/admin"];
const DISMISS_KEY = "spf_apply_bar_dismissed_at";
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 3; // 3 days

export const StickyApplyBar = () => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Check dismissal + expiry
  useEffect(() => {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (ts && Date.now() - Number(ts) < DISMISS_TTL_MS) {
      setDismissed(true);
    }
  }, []);

  // Show only after scrolling past the fold on eligible routes.
  useEffect(() => {
    if (HIDDEN_ON.some((p) => pathname.startsWith(p))) {
      setVisible(false);
      return;
    }
    const onScroll = () => setVisible(window.scrollY > 800);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  const shouldRender = visible && !dismissed && !HIDDEN_ON.some((p) => pathname.startsWith(p));

  return (
    <AnimatePresence>
      {shouldRender && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.7, 0, 0.2, 1] }}
          data-testid="sticky-apply-bar"
          className="fixed bottom-0 inset-x-0 z-40"
        >
          <div className="mx-auto max-w-[1400px] px-4 md:px-6 pb-4">
            <div className="flex items-center justify-between gap-4 bg-[color:var(--bone)] text-[color:var(--bg-elev)] px-5 md:px-6 py-3.5 md:py-4 shadow-2xl">
              <div className="flex items-center gap-4 min-w-0">
                <div className="hidden sm:block h-8 w-px bg-[color:var(--gold)]/50 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--gold)]">
                    No credit pull
                  </div>
                  <div className="mt-1 font-serif text-lg md:text-xl leading-tight truncate">
                    Apply in <em className="italic text-[color:var(--gold)]">4 minutes</em>.
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <Link
                  to="/apply"
                  data-testid="sticky-apply-cta"
                  className="group inline-flex items-center gap-2 bg-[color:var(--gold)] text-black px-5 md:px-6 py-2.5 md:py-3 font-mono text-[11px] md:text-xs tracking-[0.24em] uppercase hover:bg-[color:var(--gold-strong)] transition-colors"
                >
                  <span className="hidden sm:inline">Start application</span>
                  <span className="sm:hidden">Apply</span>
                  <ArrowRight
                    size={14}
                    className="transition-transform group-hover:translate-x-0.5"
                  />
                </Link>
                <button
                  onClick={dismiss}
                  data-testid="sticky-apply-dismiss"
                  aria-label="Dismiss"
                  className="p-2 text-[color:var(--bg-elev)]/60 hover:text-[color:var(--gold)] transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StickyApplyBar;
